import React, { useContext, useState } from 'react';
import axios from 'axios';
import { baseUrl } from '../../../baseUrl'
import Alert from '@mui/material/Alert';
import { UserContext } from '../../../Contexts/UserContext';
import { Table, Button, Upload, message, Modal, Card } from 'antd';
import { UploadOutlined, EyeOutlined, SendOutlined, InboxOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
const { Dragger } = Upload;

const BulkMessAdvanceClerk = () => {
  const [csvData, setCsvData] = useState([]);
  const [processedStatus, setProcessedStatus] = useState({});
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { setLoading } = useContext(UserContext)
  const [uploadProgress, setUploadProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      setSummary(null)
      const response = await axios.get(`${baseUrl}/warden/download-template-mess-advance`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mess_advance_template.csv');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      setError('Error downloading template: ' + error.message);
    }
  };


  const processEntries = async (addToExisting = false) => {
    setLoading(true)
    setIsLoading(true);
    try {
      const response = await axios.post(`${baseUrl}/warden/process-mess-advance`, { 
        entries: csvData, 
        addToExisting 
      });
      
      // Check if response contains warnings
      if (response.data.status === 'warnings') {
        setWarnings(response.data.warnings);
        setShowWarningModal(true);
      } else {
        // Normal processing completed
        setCsvData(csvData.map(d => {
          return { ...d, STATUS: response.data.status[d['ADMISSION_NO']] || 'Not processed' }
        }))
        setSummary(response.data.summary);
        setWarnings([]);
        setShowWarningModal(false);
      }
    } catch (err) {
      setError(`Error processing entries: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsLoading(false);
      setLoading(false)
    }
  };

  const handleProceedWithAddition = () => {
    setShowWarningModal(false);
    processEntries(true);
  };

  const handleCancelProcessing = () => {
    setShowWarningModal(false);
    setWarnings([]);
  };

  const [pageSize, setPageSize] = useState(10)

  const columns = csvData.length > 0 ? Object.keys(csvData[0]).map(key => ({
    title: key,
    dataIndex: key,
    key: key,
  })) : [];

  const props = {
    name: 'file',
    action: `${baseUrl}/warden/upload-mess-advance`,
    onChange(info) {
      if (info.file.status !== 'uploading') {
        console.log(info.file, info.fileList);
        setFile(info.file.originFileObj)
        setCsvData(info.file.response.map(row => {
          return { ...row, STATUS: 'Not processed' }
        }))
      }
      if (info.file.status === 'done') {
        message.success(`${info.file.name} file uploaded successfully`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
  };

  return (
    <div className="flex flex-col justify-start w-11/12 overflow-hidden">
      <div className="flex flex-col w-full p-4">
        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-2">Bulk Mess Advance Processing Instructions</h4>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
            <li>Prepare a CSV or Excel file with the required columns: Admission No, Name, Mess Advance Amount</li>
            <li>Ensure all admission numbers are valid and exist in the system</li>
            <li>Payment dates should be in DD/MM/YYYY format</li>
            <li>Mess advance amount should be a valid number</li>
            <li>The file size should not exceed 10MB</li>
            <li>Once uploaded, review the data before final submission</li>
          </ul>
          <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 m-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
            Download Template
          </button>
        </div>
        
        <div className='m-3'>
          <Dragger {...props} maxCount={1} accept='.csv,.xlx,.xlsx'>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">
              Support for a single or bulk upload. Strictly prohibited from uploading company data or other
              banned files.
            </p>
          </Dragger>
        </div>

        {file && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 my-6">
            <div className="flex items-center m-3">
              <span className="text-sm text-blue-700">File "{file.name}" ready for processing</span>
            </div>
            <button onClick={() => processEntries(false)} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
              Process Bulk Mess Advance
            </button>
          </div>
        )}

        {/* Warning Modal */}
        <Modal
          title={
            <div className="flex items-center">
              <ExclamationCircleOutlined className="text-yellow-500 mr-2" />
              <span>Existing Mess Advance Found</span>
            </div>
          }
          open={showWarningModal}
          onCancel={handleCancelProcessing}
          footer={[
            <Button key="cancel" onClick={handleCancelProcessing}>
              Cancel
            </Button>,
            <Button key="proceed" type="primary" className=' bg-stone-800 text-white px-4 py-3 rounded-lg text-sm' onClick={handleProceedWithAddition}>
              Add to Existing Amount
            </Button>,
          ]}
          width={800}
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Some students already have mess advance amounts in the system. Please review the details below:
            </p>
            <div className="max-h-96 overflow-y-auto">
              {warnings.map((warning, index) => (
                <Card key={index} className="mb-3" size="small">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold">Admission No: {warning.admissionNo}</p>
                      <p className="text-sm text-gray-600">
                        Existing Amount: ₹{warning.existingAmount} | 
                        New Amount: ₹{warning.newAmount} | 
                        Total if added: ₹{Number(warning.existingAmount) + Number(warning.newAmount)}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <p className="text-sm text-gray-600 bg-yellow-50 p-3 rounded">
              <strong>Note:</strong> Clicking "Add to Existing Amount" will add the new amounts to the existing mess advance amounts for all students in the list.
            </p>
          </div>
        </Modal>

        {summary && (
          <div className="my-4 p-4 bg-green-100 text-green-700 rounded-lg">
            <h3 className="font-bold">Operation Summary</h3>
            <p>Total records processed: {summary.totalRecords}</p>
            <p>Mess advance receipts created: {summary.createdReceipts}</p>
            <p>Failed entries: {summary.failedEntries}</p>
            {/* <p>Total amount processed: ₹{summary.totalAmount}</p> */}
          </div>
        )}

        {csvData.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>File Contents</h2>
            <Table
              columns={columns}
              dataSource={csvData}
              scroll={{ x: true }}
              pagination={{
                pageSize: pageSize,
                onChange: (page, pageSize) => {
                  setPageSize(pageSize)
                },
              }}
            />
          </div>
        )}
      </div>

      {uploadProgress > 0 && (
        <div className="my-4">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
          </div>
          <p className="text-sm text-gray-600 mt-1">Progress: {uploadProgress}%</p>
        </div>
      )}

      {error && (
        <Alert variant="filled" severity="warning">
          {error}
        </Alert>
      )}
    </div>
  );
};

export default BulkMessAdvanceClerk;